import * as React from 'react'
import {
    type CreateAnswerBase,
    findAnswer,
    sortChronologicallyDescending,
} from '../../../shared/domain/flows/answer.no-deps'
import { findElement } from '../../../shared/domain/flows/element.no-deps'
import * as Flow from '../../../shared/domain/flows/types.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import type { CommonDeps } from '../../../app-core/lib/base/CommonDeps'
import type { Report } from '../../../app-core/lib/flow/ExecutionPage'
import { MarkAsCompleteButton } from '../../../app-core/lib/guide/MarkAsCompleteButton'
import { cancelPromiseManagers, createPromiseManager } from '../../../app-core/lib/util/PromiseManager'
import { type PromiseState, initialPromiseState } from '../../../app-core/lib/util/PromiseState'
import { createStateLens } from '../../../app-core/lib/util/react'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'

interface Props {
    createAnswerBase: CreateAnswerBase
    deps: CommonDeps
    fetchAnswers: (reportId: Flow.ReportId) => Promise<Flow.Answer[]>
    fetchParentReport: () => Promise<Report>
    fetchSyncGuide: (guideId: Guide.Id) => Promise<Guide.SyncGuide>
    fetchWorkflowVersion: (workflowId: number) => Promise<number>
    guideElementId: Flow.ElementId
    onSubmit: () => void
    writeAnswer: (guideAnswer: Flow.GuideAnswer) => Promise<void>
    loopPathIds: LoopPathIds
    guideId?: Guide.Id
}

interface State {
    markedAsCompleteState: PromiseState<boolean>
}

function getGuideElement(flow: Flow.Flow, guideElementId: Flow.ElementId): Flow.NestedGuideElement {
    const element = findElement(flow, guideElementId)

    if (!element) {
        throw new Error(`Unable to find element with ID: ${guideElementId}`)
    }

    if (element.type !== 'guide') {
        throw new Error(`Expected to find a guide element for this ID: ${guideElementId}`)
    }

    return element
}

export class MarkGuideAsCompleteFooter extends React.Component<Props, State> {
    public state: State = {
        markedAsCompleteState: initialPromiseState,
    }

    private promiseManagers = {
        markedAsComplete: createPromiseManager(() => this.props.deps, createStateLens(this, 'markedAsCompleteState')),
    }

    public componentDidMount(): void {
        // Fetch current guide answer and see if it's marked as complete.
        const fetchMarkedAsComplete = async (): Promise<boolean> => {
            const guideAnswer: Flow.GuideAnswer = await this.fetchCurrentGuideAnswer()

            if (guideAnswer) {
                if (Array.isArray(guideAnswer.value)) {
                    const answer =
                        guideAnswer.value.find((value) => value.guideId === this.props.guideId)?.completed ?? false
                    return answer
                }
                return guideAnswer.value.completed
            } else {
                return false
            }
        }

        this.promiseManagers.markedAsComplete.trigger(fetchMarkedAsComplete)
    }

    public componentWillUnmount(): void {
        cancelPromiseManagers(this.promiseManagers)
    }

    public render(): React.ReactNode {
        return (
            <div className="AppCore__guide__MarkGuideAsCompleteFooter">
                {this.promiseManagers.markedAsComplete.render((markedAsComplete) => (
                    <MarkAsCompleteButton
                        onClick={this.toggleOnComplete}
                        markedAsComplete={markedAsComplete ?? false}
                    />
                ))}
            </div>
        )
    }

    private fetchSortedReportAnswers = (reportId: Flow.ReportId): Promise<Flow.Answer[]> =>
        this.props.fetchAnswers(reportId).then(sortChronologicallyDescending)

    private fetchCurrentGuideAnswerWithReport = async (report: Report): Promise<Flow.GuideAnswer> => {
        const reportAnswers = await this.fetchSortedReportAnswers(report.id)
        const currentAnswer = findAnswer(reportAnswers, this.props.guideElementId, this.props.loopPathIds)
        if (!Flow.isAnswerOfType('guide', currentAnswer)) {
            return Promise.reject('Expected guide answer')
        }

        return currentAnswer
    }

    private fetchCurrentGuideAnswer = async (): Promise<Flow.GuideAnswer> => {
        const parentReport = await this.props.fetchParentReport()

        return this.fetchCurrentGuideAnswerWithReport(parentReport)
    }

    private getOrCreateGuideAnswerValue = (
        guideId: Guide.Id,
        oldAnswer: Flow.GuideAnswer
    ): Promise<Flow.GuideAnswerValue | Flow.GuideAnswerValue[]> => {
        if (Flow.isAnswerOfType('guide', oldAnswer)) {
            return Promise.resolve(oldAnswer.value)
        }

        return this.props.fetchSyncGuide(guideId).then((syncGuide) => this.createGuideAnswerValue(syncGuide))
    }

    private createGuideAnswerValue = async (syncGuide: Guide.SyncGuide): Promise<Flow.GuideAnswerValue> => {
        const baseAnswer = { completed: false, guideId: syncGuide.id }

        switch (syncGuide.variant) {
            case 'file':
                const fileAnswer: Flow.FileGuideAnswer = { ...baseAnswer, type: 'file' }
                return fileAnswer
            case 'report':
                const reportAnswer: Flow.ReportGuideAnswer = { ...baseAnswer, type: 'report' }
                return reportAnswer

            case 'url':
                const urlAnswer: Flow.UrlGuideAnswer = { ...baseAnswer, type: 'url' }
                return urlAnswer

            case 'workflow':
                const workflowId = syncGuide.workflow
                const version = await this.props.fetchWorkflowVersion(workflowId)
                const workflowAnswer: Flow.WorkflowGuideAnswer = { ...baseAnswer, type: 'workflow', version }
                return workflowAnswer
        }
    }

    private toggleOnComplete = async () => {
        // Create a new guide answer where the `completed` value is flipped.
        const parentReport = await this.props.fetchParentReport()
        const currentAnswer = await this.fetchCurrentGuideAnswerWithReport(parentReport)
        const guideElement = getGuideElement(parentReport.flow, this.props.guideElementId)
        const answerValue = await this.getOrCreateGuideAnswerValue(guideElement.guideId, currentAnswer)
        const answerBase = this.props.createAnswerBase(
            guideElement.id,
            this.props.loopPathIds,
            currentAnswer,
            parentReport.id
        )

        const updatedValues = Array.isArray(answerValue)
            ? answerValue.map((value) => {
                  if (value.guideId === this.props.guideId) {
                      return {
                          ...value,
                          completed: !value.completed,
                      }
                  }
                  return value
              })
            : [answerValue]

        const answer: Flow.GuideAnswer = {
            ...answerBase,
            report: parentReport.id,
            type: 'guide',
            value: updatedValues,
        }

        // Call on parent component to write the answer value.
        await this.props.writeAnswer(answer)

        const isCompleted: boolean = Array.isArray(answer.value)
            ? (answer.value.find((value) => value.guideId === this.props.guideId)?.completed ?? false)
            : answer.value.completed

        await this.promiseManagers.markedAsComplete.update(() => isCompleted)

        // Notifiy parent component if new `completed` value is `true`.
        if (isCompleted) {
            this.props.onSubmit()
        }
    }
}
